import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, SendHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchBookingActionContext,
  submitBookingActionToken,
} from '../api/bookingActionApi';
import BookingActionShell from '../components/BookingActionShell';
import BookingMultiSlotPicker from '../components/BookingMultiSlotPicker';
import BookingSlotOptionList from '../components/BookingSlotOptionList';
import BookingSummaryCard from '../components/BookingSummaryCard';
import {
  buildSuggestedSlotPayloadsFromSlots,
  resolveAlreadyUsedActionMessage,
  resolveBookingActionMessage,
  resolveCompletedBookingAction,
} from '../utils/bookingActionUtils';
import {
  resolveBookingActionToken,
  resolveBookingSlotValue,
} from '../utils/bookingActionLinkUtils';
import { resolveHostSlotDurationMinutes } from '../../HireWithUs/Pages/Applications/meetingScheduleUtils';
import '../LiveInterview.css';

function resolveBlockedRescheduleMessage(context, actor) {
  const bookingStatus = String(context?.booking?.status || '').trim().toLowerCase();
  const sameActorConfirmed =
    (actor === 'candidate' &&
      (bookingStatus === 'accepted_by_candidate' || bookingStatus === 'confirmed')) ||
    (actor === 'interviewer' &&
      (bookingStatus === 'accepted_by_interviewer' || bookingStatus === 'confirmed'));

  if (sameActorConfirmed) {
    return 'You have already confirmed this interview from this link. Request a fresh scheduling email if you need a different time.';
  }

  return 'This link has already been used. Ask your recruiter for a fresh scheduling email if you need another change.';
}

export default function InterviewBookingReschedulePage() {
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const token = useMemo(
    () => resolveBookingActionToken({ searchParams, location, params }),
    [location, params, searchParams]
  );
  const presetSlotValue = useMemo(
    () => resolveBookingSlotValue({ searchParams, location }),
    [location, searchParams]
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [context, setContext] = useState(null);
  const [selectedSlotValue, setSelectedSlotValue] = useState('');
  const [availabilityNotes, setAvailabilityNotes] = useState('');
  const [customSuggestedSlots, setCustomSuggestedSlots] = useState([]);
  const [customSlotsResetKey, setCustomSlotsResetKey] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [result, setResult] = useState(null);

  const loadContext = useCallback(async () => {
    if (!token) {
      setError('This interview link is missing or invalid. Open the link from your email again.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = await fetchBookingActionContext(token, 'reschedule');
      setContext(payload);
      if (presetSlotValue) {
        const exists = (payload.available_slots || []).some(
          (slot) => String(slot?.value || '').trim() === presetSlotValue
        );
        if (exists) {
          setSelectedSlotValue(presetSlotValue);
        }
      }
    } catch (err) {
      if (err?.payload) {
        setContext(err.payload);
      }
      setError(err?.message || 'Unable to load this interview link.');
    } finally {
      setLoading(false);
    }
  }, [token, presetSlotValue]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const actor = context?.actor || 'candidate';
  const participantLabel = actor === 'candidate' ? 'Company' : 'Interviewer';
  const participantValue =
    actor === 'candidate' ? context?.company_label : context?.interviewer_name;
  const timezoneName = context?.timezone_name || context?.availability?.timezone || 'Asia/Kolkata';
  const hostSlotDurationMinutes = useMemo(
    () => resolveHostSlotDurationMinutes(context?.availability),
    [context?.availability]
  );

  const canSubmit =
    Boolean(selectedSlotValue) ||
    customSuggestedSlots.length > 0 ||
    String(availabilityNotes || '').trim().length >= 8;

  const showAlreadyUsedState = useCallback((nextContext) => {
    const safeContext = nextContext || context;
    const safeActor = safeContext?.actor || actor;
    setContext(safeContext || null);
    setCompleted(true);
    setResult({
      action: resolveCompletedBookingAction(safeContext) || 'reschedule',
      message: resolveAlreadyUsedActionMessage(safeContext, 'reschedule', safeActor),
      alreadyUsed: true,
    });
    setSelectedSlotValue('');
    setAvailabilityNotes('');
    setCustomSuggestedSlots([]);
    setCustomSlotsResetKey((current) => current + 1);
    setError('');
  }, [actor, context]);

  const handleSubmit = async () => {
    if (!canSubmit) {
      setError('Choose an available slot or add at least one custom time option.');
      return;
    }
    if (!token) return;
    setError('');
    setSubmitting(true);
    try {
      const suggestedSlots = buildSuggestedSlotPayloadsFromSlots(
        customSuggestedSlots,
        timezoneName,
        hostSlotDurationMinutes
      );
      const payload = await submitBookingActionToken({
        token,
        action: 'reschedule',
        slotValue: selectedSlotValue,
        availabilityNotes: selectedSlotValue ? '' : availabilityNotes,
        suggestedSlots,
      });
      const successMessage = resolveBookingActionMessage(payload, actor);
      setContext((current) => {
        if (!current) return current;
        const nextStatusLabel =
          String(payload?.actor_status_label || payload?.status_label || '').trim() ||
          'Reschedule requested';
        return {
          ...current,
          action_already_used: true,
          status_label: nextStatusLabel,
        };
      });
      setSelectedSlotValue('');
      setAvailabilityNotes('');
      setCustomSuggestedSlots([]);
      setCustomSlotsResetKey((current) => current + 1);
      setResult(payload);
      setCompleted(true);
      toast.success(successMessage);
    } catch (err) {
      if (
        err?.payload?.action_already_used ||
        err?.code === 'already_used' ||
        err?.code === 'already_confirmed' ||
        err?.code === 'already_used_cross_action'
      ) {
        if (err?.payload) {
          setContext(err.payload);
        }
        showAlreadyUsedState(err?.payload || context);
        return;
      }
      setError(err?.message || 'Unable to submit your reschedule request.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <BookingActionShell
        title="Loading reschedule"
        subtitle="Fetching available interview times..."
      >
        <div className="li-booking-loading">
          <Loader2 className="li-booking-btn-spinner" size={28} />
        </div>
      </BookingActionShell>
    );
  }

  if (!token || (error && !context)) {
    return (
      <BookingActionShell title="Link unavailable" subtitle="We could not open this reschedule page.">
        <div className="li-modal-error" role="alert">
          {error || 'This interview link is missing or invalid.'}
        </div>
      </BookingActionShell>
    );
  }

  const rescheduleAllowed = Boolean(context?.allowed_actions?.includes('reschedule'));
  const blocked =
    Boolean(context?.action_already_used) ||
    (Boolean(context) && !rescheduleAllowed);
  const blockedMessage = blocked ? resolveBlockedRescheduleMessage(context, actor) : '';
  const completedMessage =
    completed && result ? resolveBookingActionMessage(result, actor) : '';

  return (
    <BookingActionShell
      title="Request a different time"
      subtitle={
        actor === 'candidate'
          ? 'Choose a new time that works for you, or share when you are available.'
          : 'Pick a new interview time or send custom options to the candidate.'
      }
      footer={
        !completed ? (
          <Link className="li-booking-back-link" to={`/interview-booking/manage?token=${encodeURIComponent(token)}`}>
            <ArrowLeft size={16} />
            Back to interview actions
          </Link>
        ) : null
      }
    >
      {error ? (
        <div className="li-booking-inline-error" role="alert">
          {error}
        </div>
      ) : null}

      {completed && result ? (
        <div className="li-booking-inline-notice" role="status">
          <CheckCircle2 size={16} />
          <span>{completedMessage}</span>
        </div>
      ) : null}

      <BookingSummaryCard
        candidateName={context?.candidate_name}
        participantLabel={participantLabel}
        participantValue={participantValue}
        slotDisplay={context?.slot_display}
        timezoneName={timezoneName}
        statusLabel={context?.status_label}
        jobTitle={context?.job_title}
      />

      {!completed && !blocked ? (
        <>
          <BookingSlotOptionList
            slots={context?.available_slots || []}
            selectedValue={selectedSlotValue}
            disabled={submitting}
            onSelect={(slot) => {
              if (!slot) {
                setSelectedSlotValue('');
                return;
              }
              setSelectedSlotValue(String(slot?.value || '').trim());
              setCustomSuggestedSlots([]);
              setCustomSlotsResetKey((current) => current + 1);
              setAvailabilityNotes('');
            }}
          />

          {(context?.available_slots || []).length ? (
            <div className="li-booking-divider">
              <span>Or choose custom times</span>
            </div>
          ) : (
            <div className="li-booking-empty-slots">
              <h2>No open slots right now</h2>
              <p>
                Share two or more times when you are available and the scheduling team will follow up
                with a new interview plan.
              </p>
            </div>
          )}

          <BookingMultiSlotPicker
            timezone={timezoneName}
            actor={actor}
            disabled={submitting}
            slotDurationMinutes={hostSlotDurationMinutes}
            resetKey={customSlotsResetKey}
            onSlotsChange={(slots) => {
              const nextSlots = Array.isArray(slots) ? slots : [];
              setCustomSuggestedSlots(nextSlots);
              if (nextSlots.length) {
                setSelectedSlotValue('');
              }
            }}
            onNotesChange={(notes) => {
              if (notes) {
                setSelectedSlotValue('');
              }
              setAvailabilityNotes(notes);
            }}
          />

          <div className="li-booking-submit-bar">
            <button
              type="button"
              className="li-booking-btn li-booking-btn-primary"
              disabled={submitting || !canSubmit}
              onClick={handleSubmit}
            >
              {submitting ? <Loader2 className="li-booking-btn-spinner" size={16} /> : <SendHorizontal size={16} />}
              {actor === 'interviewer' ? 'Send reschedule request' : 'Send availability'}
            </button>
          </div>
        </>
      ) : null}

      {completed ? (
        <p className="li-booking-footnote">
          {result?.alreadyUsed
            ? 'This link has already been used. You can close this page now.'
            : 'Your reschedule response has been recorded. You can close this page now.'}
        </p>
      ) : blocked ? (
        <p className="li-booking-footnote">
          {blockedMessage}
        </p>
      ) : (
        <p className="li-booking-footnote">
          Each reschedule request can be submitted once from this link.
        </p>
      )}
    </BookingActionShell>
  );
}
